import { zodResolver } from '@hookform/resolvers/zod';
import {
  ExperienceLevelSchema,
  OnboardingEquipmentSchema,
  SessionDurationSchema,
  type UpdateTrainingPreferenceInput,
} from '@onflow/shared';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useOnboardingProfile, useUpdateTraining } from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

const EXPERIENCE_OPTIONS = [
  { value: 'beginner', label: 'Beginner: < 6 months training' },
  { value: 'intermediate', label: 'Intermediate: 6 months to 2 years' },
  { value: 'advanced', label: 'Advanced: 2+ years training' },
] as const;

const DURATION_OPTIONS = [
  { value: 'under_30', label: 'Under 30 min' },
  { value: '30_45', label: '30-45 min' },
  { value: '45_60', label: '45-60 min' },
  { value: '60_90', label: '60-90 min' },
  { value: '90_plus', label: '90+ min' },
] as const;

const EQUIPMENT_OPTIONS = [
  { value: 'barbell', label: 'Barbell' },
  { value: 'dumbbell', label: 'Dumbbell' },
  { value: 'machine', label: 'Machine' },
  { value: 'cable', label: 'Cable' },
  { value: 'bodyweight', label: 'Bodyweight only' },
  { value: 'resistance_bands', label: 'Resistance bands' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'other', label: 'Other' },
] as const;

// Training days are user-controlled in the tracker-first flow. AI does not
// choose or silently overwrite this value.
const TrainingFormSchema = z
  .object({
    trainingSkipped: z.boolean().default(false),
    experienceLevel: ExperienceLevelSchema.nullable().optional(),
    daysPerWeek: z.number().int().min(1).max(7).nullable().optional(),
    sessionDuration: SessionDurationSchema.nullable().optional(),
    equipment: z.array(OnboardingEquipmentSchema).default([]),
    limitations: z.string().max(1000).nullable().optional(),
    // Set on submit by the parent screen based on the user's saved environments.
    // True when home/outdoor is in the mix; false when the user trains only at
    // a gym (in which case we skip equipment selection entirely).
    showEquipment: z.boolean().default(true),
  })
  .superRefine((val, ctx) => {
    if (val.trainingSkipped) return;
    if (!val.experienceLevel) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['experienceLevel'],
        message: 'Pick your experience level',
      });
    }
    if (!val.sessionDuration) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sessionDuration'],
        message: 'Pick a session length',
      });
    }
    if (val.daysPerWeek == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['daysPerWeek'],
        message: 'Choose how many days you want to train',
      });
    }
    if (val.showEquipment && val.equipment.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['equipment'],
        message: 'Pick at least one equipment option',
      });
    }
  });

type TrainingFormValues = z.infer<typeof TrainingFormSchema>;

function parseLimitations(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 50);
}

export function TrainingScreen() {
  const navigate = useNavigate();
  const updateTraining = useUpdateTraining();
  const { data: profile } = useOnboardingProfile();
  const trainingSelected = profile?.trackingModes?.includes('training') ?? true;

  // Hide the equipment fieldset when the user only selected 'gym' on the
  // previous screen — gyms have all equipment by default, so the question is
  // dead air. Show it whenever home or outdoor is in the mix.
  const environments = profile?.environments ?? [];
  const showEquipment = environments.includes('home') || environments.includes('outdoor');

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<TrainingFormValues>({
    resolver: zodResolver(TrainingFormSchema),
    defaultValues: {
      trainingSkipped: trainingSelected ? (profile?.trainingSkipped ?? false) : true,
      experienceLevel: profile?.experienceLevel ?? undefined,
      daysPerWeek: profile?.daysPerWeek ?? 3,
      sessionDuration: profile?.sessionDuration ?? undefined,
      equipment: profile?.equipment ?? [],
      limitations: profile?.limitations?.join(', ') ?? '',
      showEquipment: true,
    },
  });

  const trainingSkipped = useWatch({ control, name: 'trainingSkipped' }) ?? false;

  // Keep the form-side showEquipment flag in sync with the visibility rule so
  // the conditional Zod refinement on equipment fires only when the fieldset
  // is actually rendered.
  useEffect(() => {
    setValue('showEquipment', showEquipment);
  }, [showEquipment, setValue]);

  function handleBack() {
    navigate('/onboarding/activity');
  }

  async function onValidSubmit(values: TrainingFormValues) {
    let payload: UpdateTrainingPreferenceInput;
    if (values.trainingSkipped) {
      payload = { trainingSkipped: true, equipment: [], limitations: [] };
    } else {
      // When the equipment section is hidden (gym-only users), we send an
      // empty array. Gym access is conveyed by `environments` (which contains
      // 'gym'), not by populating equipment with a sentinel. The downstream
      // AI prompts read both fields: environments for "what venues do they
      // train in", equipment for "what gear do they have for home/outdoor".
      // An empty equipment array alongside environments: ['gym'] is the
      // correct shape for gym-only users.
      const equipment = showEquipment ? values.equipment : [];
      payload = {
        trainingSkipped: false,
        experienceLevel: values.experienceLevel!,
        sessionDuration: values.sessionDuration!,
        equipment,
        limitations: parseLimitations(values.limitations),
        daysPerWeek: values.daysPerWeek!,
      };
    }

    try {
      await updateTraining.mutateAsync(payload);
      navigate('/onboarding/summary');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save training');
    }
  }

  const saving = updateTraining.isPending;
  return (
    <section className="onboarding-screen">
      <form
        className="onboarding-card onboarding-form"
        onSubmit={handleSubmit(onValidSubmit)}
        noValidate
      >
        <h2 className="onboarding-title">Training preferences</h2>
        <p className="onboarding-subtitle">How you want to train, or whether to skip it.</p>

        {!trainingSelected && (
          <div className="toggle-row" role="status">
            <span className="toggle-row-text">
              <span className="toggle-row-label">Training setup skipped</span>
              <span className="toggle-row-sub">
                You selected nutrition tracking only. You can enable training later.
              </span>
            </span>
          </div>
        )}

        {!trainingSkipped && (
          <>
            <fieldset className="form-group radio-group" aria-invalid={!!errors.experienceLevel}>
              <legend className="form-legend">Experience level</legend>
              {EXPERIENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="radio-option">
                  <input type="radio" value={opt.value} {...register('experienceLevel')} />
                  <span>{opt.label}</span>
                </label>
              ))}
              {errors.experienceLevel && (
                <span role="alert" className="form-error">
                  {errors.experienceLevel.message}
                </span>
              )}
            </fieldset>

            <div className="form-group">
              <label htmlFor="training-days">Training days per week</label>
              <input
                id="training-days"
                type="number"
                min={1}
                max={7}
                {...register('daysPerWeek', { valueAsNumber: true })}
              />
              {errors.daysPerWeek && (
                <span role="alert" className="form-error">
                  {errors.daysPerWeek.message}
                </span>
              )}
            </div>

            <fieldset className="form-group radio-group" aria-invalid={!!errors.sessionDuration}>
              <legend className="form-legend">Session duration</legend>
              {DURATION_OPTIONS.map((opt) => (
                <label key={opt.value} className="radio-option">
                  <input type="radio" value={opt.value} {...register('sessionDuration')} />
                  <span>{opt.label}</span>
                </label>
              ))}
              {errors.sessionDuration && (
                <span role="alert" className="form-error">
                  {errors.sessionDuration.message}
                </span>
              )}
            </fieldset>

            {showEquipment && (
              <fieldset className="form-group checkbox-group" aria-invalid={!!errors.equipment}>
                <legend className="form-legend">
                  What equipment do you have for home/outdoor workouts?
                </legend>
                <p className="form-sublabel">Pick everything you have access to.</p>
                {EQUIPMENT_OPTIONS.map((opt) => (
                  <label key={opt.value} className="checkbox-option">
                    <input type="checkbox" value={opt.value} {...register('equipment')} />
                    <span>{opt.label}</span>
                  </label>
                ))}
                {errors.equipment && (
                  <span role="alert" className="form-error">
                    {errors.equipment.message}
                  </span>
                )}
              </fieldset>
            )}

            <div className="form-group">
              <label htmlFor="training-limitations">Limitations or injuries (optional)</label>
              <textarea
                id="training-limitations"
                rows={3}
                maxLength={1000}
                placeholder="e.g., bad knees, recovering from shoulder injury, asthma"
                {...register('limitations')}
              />
            </div>
          </>
        )}

        <div className="onboarding-actions">
          <button type="button" className="btn-secondary" onClick={handleBack}>
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Next'}
          </button>
        </div>
      </form>
    </section>
  );
}
